import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, {
  MITRE_MATRICES, MITRE_MATRIX_KEYS, tMatrix, tTactic,
} from "@/lib/api";
import AppShell from "@/components/layout/AppShell";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Target, Search, Crosshair, Loader2, ExternalLink, GitBranch } from "lucide-react";

function MatrixBadge({ value }) {
  const cls = value === "ics"
    ? "bg-purple-500/15 text-purple-300 border-purple-500/30"
    : "bg-cyan-500/15 text-cyan-300 border-cyan-500/30";
  return <Badge variant="outline" className={`rounded-sm font-mono text-[10px] ${cls}`}>{tMatrix(value)}</Badge>;
}

export default function Mitre() {
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState("all");
  const [tactic, setTactic] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const { data: techniques = [], isLoading } = useQuery({
    queryKey: ["mitre-techniques"],
    queryFn: async () => (await api.get("/mitre/techniques")).data,
  });

  const { data: linkedThreats = [], isFetching: threatsLoading } = useQuery({
    queryKey: ["mitre-technique-threats", selected?.stix_id],
    queryFn: async () => (await api.get(`/mitre/techniques/${selected.stix_id}/threats`)).data,
    enabled: !!selected,
  });

  const { data: linkedScenarios = [] } = useQuery({
    queryKey: ["mitre-technique-scenarios", selected?.external_id],
    queryFn: async () => (await api.get(`/mitre/techniques/${selected.external_id}/scenarios`)).data,
    enabled: !!selected,
  });

  const tacticOptions = useMemo(() => {
    const s = new Set();
    techniques.forEach((t) => (t.tactics || []).forEach((x) => s.add(x)));
    return Array.from(s).sort();
  }, [techniques]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return techniques.filter((t) => {
      if (matrix !== "all" && t.matrix !== matrix) return false;
      if (tactic !== "all" && !(t.tactics || []).includes(tactic)) return false;
      if (needle && !(`${t.external_id} ${t.name}`.toLowerCase().includes(needle))) return false;
      return true;
    });
  }, [techniques, matrix, tactic, q]);

  return (
    <AppShell title="MITRE ATT&CK" subtitle="Référentiel de techniques — lecture seule (ATT&CK v19.2, Enterprise + ICS)">
      <div className="space-y-6" data-testid="mitre-page">
        <div className="flex flex-wrap items-end gap-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (T1486, nom…)"
              className="pl-9 rounded-sm"
              data-testid="mitre-search-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono block mb-1">Matrice</label>
            <Select value={matrix} onValueChange={setMatrix}>
              <SelectTrigger className="w-44 rounded-sm" data-testid="mitre-matrix-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {MITRE_MATRIX_KEYS.map((k) => <SelectItem key={k} value={k}>{MITRE_MATRICES[k]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono block mb-1">Tactique</label>
            <Select value={tactic} onValueChange={setTactic}>
              <SelectTrigger className="w-56 rounded-sm" data-testid="mitre-tactic-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {tacticOptions.map((k) => <SelectItem key={k} value={k}>{tTactic(k)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <p className="text-xs text-muted-foreground" data-testid="mitre-count">
          {filtered.length} technique(s) — référentiel officiel MITRE ATT&CK, non modifiable (chargé par un loader dédié).
        </p>

        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
        ) : (
          <div className="border border-border rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="w-28">Identifiant</TableHead>
                  <TableHead>Technique</TableHead>
                  <TableHead className="w-28">Matrice</TableHead>
                  <TableHead>Tactiques</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="mitre-table">
                {filtered.map((t) => (
                  <TableRow
                    key={t.stix_id}
                    className="border-border cursor-pointer"
                    onClick={() => setSelected(t)}
                    data-testid={`mitre-row-${t.external_id}`}
                  >
                    <TableCell className="font-mono text-xs text-primary">{t.external_id}</TableCell>
                    <TableCell className="font-medium">
                      {t.name}
                      {t.is_subtechnique && t.parent_external_id && (
                        <span className="ml-2 text-[10px] font-mono text-muted-foreground">↳ {t.parent_external_id}</span>
                      )}
                    </TableCell>
                    <TableCell><MatrixBadge value={t.matrix} /></TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(t.tactics || []).map((x) => (
                          <Badge key={x} variant="outline" className="rounded-sm text-[10px] font-mono">{tTactic(x)}</Badge>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-sm text-muted-foreground py-8 text-center">Aucune technique ne correspond aux filtres.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="mitre-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-head flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  <span className="font-mono text-primary">{selected.external_id}</span> {selected.name}
                </DialogTitle>
                <DialogDescription>Référentiel MITRE ATT&CK (lecture seule). Aucun risque/score/scénario n'est dérivé.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-1 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Matrice</div><div className="mt-1"><MatrixBadge value={selected.matrix} /></div></div>
                  <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Version</div><div className="mt-1 font-mono text-xs">v{selected.version} · {selected.dataset_version}</div></div>
                  <div className="col-span-2"><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">STIX ID</div><div className="mt-1 font-mono text-[11px] break-all" data-testid="mitre-detail-stix">{selected.stix_id}</div></div>
                  <div className="col-span-2">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Tactiques</div>
                    <div className="mt-1 flex flex-wrap gap-1">{(selected.tactics || []).map((x) => <Badge key={x} variant="outline" className="rounded-sm text-[10px] font-mono">{tTactic(x)}</Badge>)}</div>
                  </div>
                  {selected.is_subtechnique && (
                    <div className="col-span-2"><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Technique parente</div><div className="mt-1 font-mono text-xs">{selected.parent_external_id}</div></div>
                  )}
                </div>
                {selected.description && <p className="text-xs text-muted-foreground leading-relaxed border-t border-border pt-3">{selected.description}</p>}
                <div className="border-t border-border pt-3">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono mb-2 flex items-center gap-1.5"><Crosshair className="h-3.5 w-3.5" /> Menaces associées (votre organisation)</div>
                  {threatsLoading ? (
                    <div className="text-xs text-muted-foreground font-mono flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement…</div>
                  ) : linkedThreats.length === 0 ? (
                    <p className="text-xs text-muted-foreground" data-testid="mitre-detail-no-threats">Aucune menace de votre organisation n'utilise cette technique.</p>
                  ) : (
                    <div className="space-y-1.5" data-testid="mitre-detail-threats">
                      {linkedThreats.map((th) => (
                        <button
                          key={th.id}
                          onClick={() => { setSelected(null); navigate(`/threats/${th.id}`); }}
                          className="w-full text-left flex items-center gap-2 text-sm hover:text-primary"
                          data-testid={`mitre-threat-nav-${th.id}`}
                        >
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" /> {th.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="border-t border-border pt-3">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono mb-2 flex items-center gap-1.5"><GitBranch className="h-3.5 w-3.5" /> Scénarios liés (votre organisation)</div>
                  {linkedScenarios.length === 0 ? (
                    <p className="text-xs text-muted-foreground" data-testid="mitre-detail-no-scenarios">Aucun scénario n'utilise cette technique.</p>
                  ) : (
                    <div className="space-y-1.5" data-testid="mitre-detail-scenarios">
                      {linkedScenarios.map((sc) => (
                        <button key={sc.id} onClick={() => { setSelected(null); navigate(`/scenarios/${sc.id}`); }}
                          className="w-full text-left flex items-center gap-2 text-sm hover:text-primary"
                          data-testid={`mitre-scenario-nav-${sc.id}`}>
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" /> {sc.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
